import {
  getSnapFilterById,
  hasActiveSnapLens,
} from "../snap/config/snapFilters";

const SNAP_TOKEN_PLACEHOLDER = "PUT_STAGING_SNAP_API_TOKEN_HERE";
const PROBE_LENS_FILTER_ID = "glow_whitening";
const APPLY_DELAY_MS = 2500;
const PREP_TIMEOUT_MS = 6000;
const SWAP_TIMEOUT_MS = 3000;
const ACTIVE_LIMIT_MS = 15000;
const PROBE_FPS = 8;
const STABILITY_FRAMES_NEEDED = 2;
const STABILITY_POLL_MS = 120;
/** @type {0} ZegoStreamUpdateType.video — video only, keep mic */
const ZEGO_PUBLISH_UPDATE_VIDEO = 0;

let activeRealApplyAttemptId = 0;
let realApplyProbeInFlight = false;
/** @type {{ cancelled: boolean } | null} */
let activeRunState = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let applyDelayTimerId = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let activeLimitTimerId = null;

/** @type {{
 *   cameraKit: object | null,
 *   session: object | null,
 *   processedStream: MediaStream | null,
 *   rawVideoOnlyStream: MediaStream | null,
 *   probeVideo: HTMLVideoElement | null,
 *   publishSwapped: boolean,
 *   zegoEngine: object | null,
 *   zegoLocalStream: object | null,
 *   terminalLogged: boolean,
 *   startedAt: number,
 *   hasActiveRun: boolean,
 * }} */
const runtime = {
  cameraKit: null,
  session: null,
  processedStream: null,
  rawVideoOnlyStream: null,
  probeVideo: null,
  publishSwapped: false,
  zegoEngine: null,
  zegoLocalStream: null,
  terminalLogged: false,
  startedAt: 0,
  hasActiveRun: false,
};

function isSnapRealApplyProbeEnabled() {
  const raw = String(process.env.REACT_APP_SNAP_CALL_FILTER_REAL_APPLY_PROBE || "")
    .trim()
    .toLowerCase();
  return raw === "true";
}

function isProbeSnapTokenConfigured() {
  const token = String(process.env.REACT_APP_SNAP_API_TOKEN || "").trim();
  return Boolean(token && token !== SNAP_TOKEN_PLACEHOLDER);
}

function resolveProbeLensTarget() {
  const filter = getSnapFilterById(PROBE_LENS_FILTER_ID);
  if (!filter || !hasActiveSnapLens(filter)) {
    return null;
  }
  return {
    lensId: filter.lensId,
    lensGroupId: filter.lensGroupId,
  };
}

function logTerminal(message) {
  if (runtime.terminalLogged) {
    return;
  }
  runtime.terminalLogged = true;
  console.log(message);
}

function throwIfCancelled(runState, attemptId) {
  if (runState.cancelled || attemptId !== activeRealApplyAttemptId) {
    throw Object.assign(new Error("aborted"), { code: "ABORTED" });
  }
}

function clearApplyDelayTimer() {
  if (applyDelayTimerId) {
    clearTimeout(applyDelayTimerId);
    applyDelayTimerId = null;
  }
}

function clearActiveLimitTimer() {
  if (activeLimitTimerId) {
    clearTimeout(activeLimitTimerId);
    activeLimitTimerId = null;
  }
}

function stopProcessedStreamTracks() {
  runtime.processedStream?.getVideoTracks?.().forEach((track) => {
    try {
      track.stop();
    } catch {
      // ignore
    }
  });
  runtime.processedStream = null;
}

function removeProbeVideo() {
  if (!runtime.probeVideo) {
    return;
  }
  try {
    runtime.probeVideo.pause();
  } catch {
    // ignore
  }
  runtime.probeVideo.srcObject = null;
  runtime.probeVideo.remove();
  runtime.probeVideo = null;
}

/**
 * @param {object | null | undefined} zegoLocalStream
 * @returns {MediaStream | null}
 */
function extractZegoInputMediaStream(zegoLocalStream) {
  if (!zegoLocalStream) {
    return null;
  }

  const wrappedStream = zegoLocalStream.stream;
  if (wrappedStream?.getVideoTracks?.()?.length) {
    return wrappedStream;
  }

  const capture = zegoLocalStream.videoCaptureStream;
  if (capture?.getVideoTracks?.()?.length) {
    return capture;
  }

  const track = zegoLocalStream.getVideoTracks?.()?.[0];
  if (track) {
    return new MediaStream([track]);
  }

  return null;
}

/**
 * Video-only stream referencing original Zego camera tracks (tracks are not stopped).
 * @param {MediaStream | null} originalMediaStream
 * @returns {MediaStream | null}
 */
function createRawVideoOnlyStream(originalMediaStream) {
  const videoTracks = originalMediaStream?.getVideoTracks?.() || [];
  if (!videoTracks.length) {
    return null;
  }
  return new MediaStream(videoTracks);
}

function isZegoCustomVideoSwapSupported(zegoLocalStream, zegoEngine) {
  return (
    typeof zegoLocalStream?.startCaptureCustomVideo === "function" &&
    typeof zegoEngine?.updatePublishingStream === "function"
  );
}

function isRawRestoreSupported(zegoLocalStream, zegoEngine, rawVideoOnlyStream) {
  return (
    isZegoCustomVideoSwapSupported(zegoLocalStream, zegoEngine) &&
    Boolean(rawVideoOnlyStream?.getVideoTracks?.()?.length)
  );
}

function isZegoVideoSwapSupported(zegoLocalStream, zegoEngine) {
  return isZegoCustomVideoSwapSupported(zegoLocalStream, zegoEngine);
}

function hasProbeCleanupWork() {
  return (
    runtime.hasActiveRun ||
    runtime.publishSwapped ||
    Boolean(runtime.cameraKit || runtime.session || runtime.processedStream) ||
    realApplyProbeInFlight ||
    Boolean(applyDelayTimerId || activeLimitTimerId)
  );
}

/**
 * @param {MediaStream} stream
 * @param {number} neededFrames
 * @param {number} timeoutMs
 * @param {{ cancelled: boolean }} runState
 * @param {number} attemptId
 */
async function waitForProcessedVideoFrames(stream, neededFrames, timeoutMs, runState, attemptId) {
  const track = stream?.getVideoTracks?.()?.[0];
  if (!track || track.readyState !== "live" || track.muted) {
    return false;
  }

  removeProbeVideo();
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.setAttribute("playsinline", "true");
  video.style.cssText =
    "position:fixed;opacity:0;pointer-events:none;width:1px;height:1px;left:-9999px;top:-9999px;";
  document.body.appendChild(video);
  runtime.probeVideo = video;
  video.srcObject = stream;

  try {
    await video.play();
  } catch {
    return false;
  }

  const deadline = performance.now() + timeoutMs;
  let frameCount = 0;
  let lastMediaTime = -1;
  let lastCurrentTime = -1;

  return new Promise((resolve) => {
    let settled = false;
    let pollId = null;

    const finish = (ok) => {
      if (settled) {
        return;
      }
      settled = true;
      if (pollId) {
        clearInterval(pollId);
      }
      resolve(ok);
    };

    const onFrame = (_now, metadata) => {
      if (runState.cancelled || attemptId !== activeRealApplyAttemptId) {
        finish(false);
        return;
      }
      if (performance.now() > deadline) {
        finish(false);
        return;
      }

      const mediaTime = metadata?.mediaTime ?? video.currentTime;
      if (mediaTime !== lastMediaTime) {
        frameCount += 1;
        lastMediaTime = mediaTime;
        if (frameCount >= neededFrames) {
          finish(true);
          return;
        }
      }

      if (typeof video.requestVideoFrameCallback === "function") {
        video.requestVideoFrameCallback(onFrame);
      }
    };

    if (typeof video.requestVideoFrameCallback === "function") {
      video.requestVideoFrameCallback(onFrame);
      return;
    }

    pollId = setInterval(() => {
      if (runState.cancelled || attemptId !== activeRealApplyAttemptId) {
        finish(false);
        return;
      }
      if (performance.now() > deadline) {
        finish(false);
        return;
      }

      const liveTrack = video.srcObject?.getVideoTracks?.()?.[0];
      if (!liveTrack || liveTrack.readyState !== "live" || liveTrack.muted) {
        finish(false);
        return;
      }

      if (video.currentTime > lastCurrentTime) {
        frameCount += 1;
        lastCurrentTime = video.currentTime;
        if (frameCount >= neededFrames) {
          finish(true);
        }
      }
    }, STABILITY_POLL_MS);
  });
}


/**
 * @param {Promise<unknown>} promise
 * @param {number} timeoutMs
 * @param {{ cancelled: boolean }} runState
 */
function raceWithTimeout(promise, timeoutMs, runState) {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      runState.cancelled = true;
      reject(Object.assign(new Error("timeout"), { code: "TIMEOUT", timeoutMs }));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
}

/**
 * @param {object} zegoLocalStream
 * @param {object} zegoEngine
 * @returns {Promise<boolean>}
 */
async function restoreRawZegoVideo(zegoLocalStream, zegoEngine) {
  if (!runtime.publishSwapped || !zegoLocalStream || !zegoEngine) {
    return false;
  }

  const rawVideoOnlyStream = runtime.rawVideoOnlyStream;
  if (!isRawRestoreSupported(zegoLocalStream, zegoEngine, rawVideoOnlyStream)) {
    runtime.publishSwapped = false;
    return false;
  }

  try {
    await zegoLocalStream.startCaptureCustomVideo({
      source: rawVideoOnlyStream,
      optimizationMode: "motion",
    });
    await zegoEngine.updatePublishingStream(zegoLocalStream, ZEGO_PUBLISH_UPDATE_VIDEO);
    runtime.publishSwapped = false;
    return true;
  } catch {
    runtime.publishSwapped = false;
    return false;
  }
}

async function destroyProbeCameraKit() {
  stopProcessedStreamTracks();
  removeProbeVideo();

  const session = runtime.session;
  runtime.session = null;
  if (session) {
    try {
      await session.pause?.("capture");
    } catch {
      // ignore
    }
    try {
      await session.pause?.("live");
    } catch {
      // ignore
    }
    try {
      await session.removeLens?.();
    } catch {
      // ignore
    }
    try {
      await session.destroy?.();
    } catch {
      // ignore
    }
  }

  const cameraKit = runtime.cameraKit;
  runtime.cameraKit = null;
  if (cameraKit) {
    try {
      await cameraKit.destroy?.();
    } catch {
      // ignore
    }
  }
}

/**
 * @param {string} [reason]
 */
export async function cleanupSnapRealApplyProbe(_reason = "unknown") {
  if (!hasProbeCleanupWork()) {
    return;
  }

  clearApplyDelayTimer();
  clearActiveLimitTimer();

  const zegoLocalStream = runtime.zegoLocalStream;
  const zegoEngine = runtime.zegoEngine;
  const hadSwap = runtime.publishSwapped;

  let restored = false;
  if (hadSwap && zegoLocalStream && zegoEngine) {
    restored = await restoreRawZegoVideo(zegoLocalStream, zegoEngine);
  }

  await destroyProbeCameraKit();

  runtime.zegoEngine = null;
  runtime.zegoLocalStream = null;
  runtime.rawVideoOnlyStream = null;
  runtime.hasActiveRun = false;
  realApplyProbeInFlight = false;

  if (!runtime.terminalLogged && hadSwap) {
    logTerminal(`[snapRealApplyProbe] restoredRaw=${restored}`);
  }

  if (activeRunState) {
    activeRunState.cancelled = true;
    activeRunState = null;
  }
}

/**
 * @param {string} [reason]
 */
export function invalidateSnapRealApplyProbe(_reason = "unknown") {
  activeRealApplyAttemptId += 1;
  if (activeRunState) {
    activeRunState.cancelled = true;
  }
  clearApplyDelayTimer();
  clearActiveLimitTimer();
}

/**
 * @param {{
 *   callId?: string,
 *   zegoEngine?: object,
 *   zegoLocalStream?: object,
 *   streamID?: string,
 * }} params
 */
export async function runSnapRealApplyProbe(params = {}) {
  try {
    const enabled = isSnapRealApplyProbeEnabled();
    console.log(`[snapRealApplyProbe] enabled=${enabled}`);
    if (!enabled) {
      return;
    }

    const tokenConfigured = isProbeSnapTokenConfigured();
    console.log(`[snapRealApplyProbe] tokenConfigured=${tokenConfigured}`);
    console.log(`[snapRealApplyProbe] selectedFilter=${PROBE_LENS_FILTER_ID}`);

    const lensTarget = resolveProbeLensTarget();
    const lensTargetResolved = Boolean(lensTarget);
    console.log(`[snapRealApplyProbe] lensTargetResolved=${lensTargetResolved}`);

    if (!tokenConfigured) {
      logTerminal("[snapRealApplyProbe] failed reason=missing_token");
      return;
    }

    if (!lensTargetResolved) {
      logTerminal("[snapRealApplyProbe] failed reason=lens_target_unresolved");
      return;
    }

    if (realApplyProbeInFlight) {
      return;
    }

    const { zegoEngine, zegoLocalStream } = params;
    if (!zegoEngine || !zegoLocalStream) {
      logTerminal("[snapRealApplyProbe] failed reason=zego_context_missing");
      return;
    }

    const attemptId = activeRealApplyAttemptId;
    const runState = { cancelled: false };
    activeRunState = runState;
    realApplyProbeInFlight = true;
    runtime.terminalLogged = false;
    runtime.publishSwapped = false;
    runtime.hasActiveRun = true;
    runtime.rawVideoOnlyStream = null;
    runtime.zegoEngine = zegoEngine;
    runtime.zegoLocalStream = zegoLocalStream;
    runtime.startedAt = performance.now();

    console.log("[snapRealApplyProbe] started");

    applyDelayTimerId = setTimeout(() => {
      applyDelayTimerId = null;
      void executeRealApplyProbe({
        attemptId,
        runState,
        lensTarget,
        zegoEngine,
        zegoLocalStream,
      });
    }, APPLY_DELAY_MS);
  } catch {
    realApplyProbeInFlight = false;
    activeRunState = null;
    logTerminal("[snapRealApplyProbe] failed reason=unexpected");
  }
}

/**
 * @param {{
 *   attemptId: number,
 *   runState: { cancelled: boolean },
 *   lensTarget: { lensId: string, lensGroupId: string },
 *   zegoEngine: object,
 *   zegoLocalStream: object,
 * }} ctx
 */
async function executeRealApplyProbe(ctx) {
  const { attemptId, runState, lensTarget, zegoEngine, zegoLocalStream } = ctx;

  if (attemptId !== activeRealApplyAttemptId || runState.cancelled) {
    realApplyProbeInFlight = false;
    runtime.hasActiveRun = false;
    return;
  }

  const milestone = {
    sourceResolved: false,
    cameraKitReady: false,
    lensLoaded: false,
    sessionCreated: false,
    sourceSet: false,
    lensApplied: false,
    outputReady: false,
    rawRestoreSupported: false,
    zegoVideoSwapSupported: isZegoVideoSwapSupported(zegoLocalStream, zegoEngine),
    zegoVideoSwapAttempted: false,
    zegoVideoSwapSuccess: false,
  };

  const logMilestones = () => {
    console.log(`[snapRealApplyProbe] sourceResolved=${milestone.sourceResolved}`);
    console.log(`[snapRealApplyProbe] cameraKitReady=${milestone.cameraKitReady}`);
    console.log(`[snapRealApplyProbe] lensLoaded=${milestone.lensLoaded}`);
    console.log(`[snapRealApplyProbe] sessionCreated=${milestone.sessionCreated}`);
    console.log(`[snapRealApplyProbe] sourceSet=${milestone.sourceSet}`);
    console.log(`[snapRealApplyProbe] lensApplied=${milestone.lensApplied}`);
    console.log(`[snapRealApplyProbe] outputReady=${milestone.outputReady}`);
    console.log(`[snapRealApplyProbe] rawRestoreSupported=${milestone.rawRestoreSupported}`);
    console.log(
      `[snapRealApplyProbe] zegoVideoSwapSupported=${milestone.zegoVideoSwapSupported}`
    );
    console.log(
      `[snapRealApplyProbe] zegoVideoSwapAttempted=${milestone.zegoVideoSwapAttempted}`
    );
    console.log(`[snapRealApplyProbe] zegoVideoSwapSuccess=${milestone.zegoVideoSwapSuccess}`);
    console.log(`[snapRealApplyProbe] activeLimitedMs=${ACTIVE_LIMIT_MS}`);
  };

  const fail = async (reason) => {
    logMilestones();
    await destroyProbeCameraKit();
    if (runtime.publishSwapped) {
      const restored = await restoreRawZegoVideo(zegoLocalStream, zegoEngine);
      logTerminal(`[snapRealApplyProbe] restoredRaw=${restored}`);
    } else {
      logTerminal(`[snapRealApplyProbe] failed reason=${reason}`);
    }
    realApplyProbeInFlight = false;
    runtime.hasActiveRun = false;
    if (activeRunState === runState) {
      activeRunState = null;
    }
  };

  try {
    throwIfCancelled(runState, attemptId);

    const originalMediaStream = extractZegoInputMediaStream(zegoLocalStream);
    milestone.sourceResolved = Boolean(originalMediaStream?.getVideoTracks?.()?.length);
    if (!milestone.sourceResolved) {
      await fail("source_unresolved");
      return;
    }

    runtime.rawVideoOnlyStream = createRawVideoOnlyStream(originalMediaStream);
    milestone.rawRestoreSupported = isRawRestoreSupported(
      zegoLocalStream,
      zegoEngine,
      runtime.rawVideoOnlyStream
    );
    console.log(`[snapRealApplyProbe] rawRestoreSupported=${milestone.rawRestoreSupported}`);

    if (!milestone.rawRestoreSupported) {
      await fail("raw_restore_unsupported");
      return;
    }

    const prepPromise = (async () => {
      let bootstrapCameraKit;
      let createMediaStreamSource;
      try {
        ({ bootstrapCameraKit, createMediaStreamSource } = await import("@snap/camera-kit"));
      } catch {
        throw Object.assign(new Error("import_failed"), { code: "IMPORT_FAILED" });
      }
      throwIfCancelled(runState, attemptId);

      const apiToken = String(process.env.REACT_APP_SNAP_API_TOKEN || "").trim();
      let cameraKit;
      try {
        cameraKit = await bootstrapCameraKit({ apiToken, logLevel: "warn" });
      } catch {
        throw Object.assign(new Error("bootstrap_error"), { code: "BOOTSTRAP_ERROR" });
      }
      runtime.cameraKit = cameraKit;
      milestone.cameraKitReady = true;
      throwIfCancelled(runState, attemptId);

      let lens;
      try {
        lens = await cameraKit.lensRepository.loadLens(
          lensTarget.lensId,
          lensTarget.lensGroupId
        );
      } catch {
        throw Object.assign(new Error("lens_load_failed"), { code: "LENS_LOAD_FAILED" });
      }
      milestone.lensLoaded = true;
      throwIfCancelled(runState, attemptId);

      let session;
      try {
        session = await cameraKit.createSession();
      } catch {
        throw Object.assign(new Error("session_creation_failed"), { code: "SESSION_FAILED" });
      }
      runtime.session = session;
      milestone.sessionCreated = true;
      throwIfCancelled(runState, attemptId);

      let source;
      try {
        source = createMediaStreamSource(originalMediaStream, {
          disableSourceAudio: true,
        });
      } catch {
        throw Object.assign(new Error("source_creation_failed"), { code: "SOURCE_CREATE_FAILED" });
      }

      try {
        await session.setSource(source);
      } catch {
        throw Object.assign(new Error("set_source_failed"), { code: "SET_SOURCE_FAILED" });
      }
      milestone.sourceSet = true;
      throwIfCancelled(runState, attemptId);

      if (typeof session.setFPSLimit === "function") {
        try {
          await session.setFPSLimit(PROBE_FPS);
        } catch {
          // non-fatal
        }
      }

      try {
        const applied = await session.applyLens(lens);
        milestone.lensApplied = applied === true;
        if (!milestone.lensApplied) {
          throw Object.assign(new Error("apply_lens_failed"), { code: "APPLY_LENS_FAILED" });
        }
      } catch (err) {
        if (err?.code === "APPLY_LENS_FAILED") {
          throw err;
        }
        throw Object.assign(new Error("apply_lens_failed"), { code: "APPLY_LENS_FAILED" });
      }
      throwIfCancelled(runState, attemptId);

      try {
        await session.play("capture");
      } catch {
        throw Object.assign(new Error("play_failed"), { code: "PLAY_FAILED" });
      }

      const captureCanvas = session.output?.capture;
      if (!captureCanvas) {
        throw Object.assign(new Error("output_not_ready"), { code: "OUTPUT_NOT_READY" });
      }

      if (!captureCanvas.width || !captureCanvas.height) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
      }

      milestone.outputReady = Boolean(
        session.playing?.capture &&
          captureCanvas.width > 0 &&
          captureCanvas.height > 0
      );

      if (!milestone.outputReady) {
        throw Object.assign(new Error("output_not_ready"), { code: "OUTPUT_NOT_READY" });
      }

      if (typeof captureCanvas.captureStream !== "function") {
        throw Object.assign(new Error("capture_stream_unsupported"), {
          code: "CAPTURE_STREAM_UNSUPPORTED",
        });
      }

      stopProcessedStreamTracks();
      const processedStream = captureCanvas.captureStream(PROBE_FPS);
      runtime.processedStream = processedStream;

      if (!processedStream?.getVideoTracks?.()?.length) {
        throw Object.assign(new Error("output_not_ready"), { code: "OUTPUT_NOT_READY" });
      }

      const stable = await waitForProcessedVideoFrames(
        processedStream,
        STABILITY_FRAMES_NEEDED,
        PREP_TIMEOUT_MS,
        runState,
        attemptId
      );
      if (!stable) {
        throw Object.assign(new Error("stability_failed"), { code: "STABILITY_FAILED" });
      }

      return processedStream;
    })();

    let processedStream;
    try {
      processedStream = await raceWithTimeout(prepPromise, PREP_TIMEOUT_MS, runState);
      prepPromise.catch(() => {});
    } catch (err) {
      prepPromise.catch(() => {});
      if (err?.code === "TIMEOUT") {
        logMilestones();
        await destroyProbeCameraKit();
        logTerminal(`[snapRealApplyProbe] timeout ms=${PREP_TIMEOUT_MS}`);
        realApplyProbeInFlight = false;
        runtime.hasActiveRun = false;
        if (activeRunState === runState) {
          activeRunState = null;
        }
        return;
      }
      if (err?.code === "ABORTED") {
        return;
      }

      const reasonMap = {
        IMPORT_FAILED: "import_failed",
        BOOTSTRAP_ERROR: "bootstrap_error",
        LENS_LOAD_FAILED: "lens_load_failed",
        SESSION_FAILED: "session_creation_failed",
        SOURCE_CREATE_FAILED: "source_creation_failed",
        SET_SOURCE_FAILED: "set_source_failed",
        APPLY_LENS_FAILED: "apply_lens_failed",
        PLAY_FAILED: "play_failed",
        OUTPUT_NOT_READY: "output_not_ready",
        CAPTURE_STREAM_UNSUPPORTED: "capture_stream_unsupported",
        STABILITY_FAILED: "stability_failed",
      };
      await fail(reasonMap[err?.code] || "prep_failed");
      return;
    }

    throwIfCancelled(runState, attemptId);

    if (!milestone.zegoVideoSwapSupported) {
      logMilestones();
      await destroyProbeCameraKit();
      logTerminal("[snapRealApplyProbe] failed reason=zego_swap_unsupported");
      realApplyProbeInFlight = false;
      runtime.hasActiveRun = false;
      if (activeRunState === runState) {
        activeRunState = null;
      }
      return;
    }

    milestone.zegoVideoSwapAttempted = true;

    const swapPromise = (async () => {
      await zegoLocalStream.startCaptureCustomVideo({
        source: processedStream,
        optimizationMode: "motion",
      });
      await zegoEngine.updatePublishingStream(zegoLocalStream, ZEGO_PUBLISH_UPDATE_VIDEO);
    })();

    try {
      await raceWithTimeout(swapPromise, SWAP_TIMEOUT_MS, runState);
      swapPromise.catch(() => {});
      milestone.zegoVideoSwapSuccess = true;
      runtime.publishSwapped = true;
    } catch (err) {
      swapPromise.catch(() => {});
      if (err?.code === "TIMEOUT") {
        logMilestones();
        await destroyProbeCameraKit();
        logTerminal(`[snapRealApplyProbe] timeout ms=${SWAP_TIMEOUT_MS}`);
        realApplyProbeInFlight = false;
        runtime.hasActiveRun = false;
        if (activeRunState === runState) {
          activeRunState = null;
        }
        return;
      }
      if (err?.code === "ABORTED") {
        return;
      }
      await fail("zego_swap_failed");
      return;
    }

    logMilestones();

    activeLimitTimerId = setTimeout(() => {
      activeLimitTimerId = null;
      void finishActiveRealApplyProbe(runState, attemptId, zegoEngine, zegoLocalStream);
    }, ACTIVE_LIMIT_MS);
  } catch (err) {
    if (err?.code === "ABORTED") {
      return;
    }
    await fail("unexpected");
  }
}

/**
 * @param {{ cancelled: boolean }} runState
 * @param {number} attemptId
 * @param {object} zegoEngine
 * @param {object} zegoLocalStream
 */
async function finishActiveRealApplyProbe(runState, attemptId, zegoEngine, zegoLocalStream) {
  if (runState.cancelled || attemptId !== activeRealApplyAttemptId) {
    return;
  }

  const restored = await restoreRawZegoVideo(zegoLocalStream, zegoEngine);
  await destroyProbeCameraKit();

  const ms = Math.round(performance.now() - runtime.startedAt);
  logTerminal(`[snapRealApplyProbe] success ms=${ms}`);
  console.log(`[snapRealApplyProbe] restoredRaw=${restored}`);

  realApplyProbeInFlight = false;
  runtime.hasActiveRun = false;
  runtime.zegoEngine = null;
  runtime.zegoLocalStream = null;
  runtime.rawVideoOnlyStream = null;
  if (activeRunState === runState) {
    activeRunState = null;
  }
}
